import { useQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { Calendar, TileDisabledFunc } from 'react-calendar';

import { getEventTicketDetails } from '@apiCalls';
import { getEventByDate } from '@pages/event/[eventId]/checkout/date-select';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];
interface DateSelectorProperties {
  events: IEvent[];
  onEventDateSelect: (date: Date) => void;
}

export const DateSelector = ({
  events,
  onEventDateSelect,
}: DateSelectorProperties) => {
  const [value, setValue] = useState<Value>(new Date());
  const queries = events.map(event => ({
    queryKey: ['tickets', String(event.ID)],
    queryFn: () => getEventTicketDetails(event.ID),
    enabled: Number.isFinite(event.ID) && event.ID > 0,
  }));
  const ticketsData = useQueries({ queries });
  const tickets = useMemo(() => {
    return ticketsData.map(query => query.data).filter(Boolean) as ITicket[][];
  }, [ticketsData]);

  const { min, max } = getDateLimits(events);
  const tileDisabled: TileDisabledFunc = ({ date, view }) => {
    const currentDate = dayjs(date);
    if (view === 'month' && currentDate.isBefore(dayjs(), 'day')) {
      return true;
    }
    if (view === 'month') {
      return !events.some(event =>
        dayjs(event.Date).isSame(currentDate, 'day')
      );
    }
    const event = getEventByDate(events, date);
    const queryIndex = queries.findIndex(
      query => query.queryKey[1] === String(event?.ID)
    );
    const eventTickets = tickets[queryIndex];
    const isAnyTicketAvailable = eventTickets?.some(
      ticket => ticket.Remaining > 0
    );
    if (!isAnyTicketAvailable) {
      return true;
    }
    return false;
  };

  const handleDateSelect = (value: Value) => {
    setValue(value);
    if (Array.isArray(value)) {
      return null;
    }
    onEventDateSelect(value as Date);
  };

  if (ticketsData.some(query => query.status === 'pending')) {
    return <div>Loading...</div>;
  }

  if (ticketsData.some(query => query.status === 'error')) {
    return <div>Error loading tickets</div>;
  }

  return (
    <Calendar
      onChange={handleDateSelect}
      value={value}
      prev2Label={null}
      next2Label={null}
      defaultActiveStartDate={undefined}
      minDate={min}
      maxDate={max}
      minDetail='year'
      calendarType='iso8601'
      tileDisabled={tileDisabled}
    />
  );
};

const getDateLimits = (events: IEvent[]) => {
  let earliestDate = dayjs(events[0]?.Date).toDate();
  let latestDate = dayjs(events[0]?.Date).toDate();

  for (const object of events) {
    const currentDate = dayjs(object.Date);
    if (currentDate.isBefore(earliestDate)) {
      earliestDate = currentDate.toDate();
    }
    if (currentDate.isAfter(latestDate)) {
      latestDate = currentDate.toDate();
    }
  }

  return { min: earliestDate, max: latestDate };
};
